/**
 * @file RedirectsList.tsx
 * @description Список переадресаций с состояниями (phase19.16, партия Р3в,
 * v0.19.64; SB131; uiux-r3 §3.5): загрузка — заготовки строк; ошибка —
 * «Не удалось загрузить… [Повторить]»; пусто — «Переадресаций нет. Они
 * появятся сами, когда вы смените адрес страницы, товара или статьи»;
 * поиск ничего не нашёл — своя фраза; больше 50 — «Показать ещё».
 * С 768 — заголовок колонок над строками.
 */

import { Button } from "../../ui/button";
import { LIST_FRAME_CLASS, ListError, ListSkeleton } from "../../ui/ListStates";
import { REDIRECT_ROW_GRID, RedirectRow } from "./RedirectRow";
import type { RedirectsApi } from "../../../hooks/site-seo/useRedirects";
import type { SiteRedirectItem } from "../../../lib/api-site-redirects";
import { cn } from "@/lib/utils";

interface Props {
  api: RedirectsApi;
  canEdit: boolean;
  onEdit: (item: SiteRedirectItem) => void;
  onProbe: (path: string) => void;
  onRemove: (item: SiteRedirectItem) => void;
}

export function RedirectsList({ api, canEdit, onEdit, onProbe, onRemove }: Props) {
  if (api.error && !api.items) return <ListError message={api.error} onRetry={() => void api.reload()} />;
  if (!api.items) return <ListSkeleton rows={4} compact />;
  const filtered = api.query.trim() !== "" || api.filter !== "all";
  if (api.items.length === 0) {
    return (
      <p className="type-body rounded-xl bg-surface-low p-4 text-on-surface-variant">
        {filtered ? "Ничего не нашлось — измените поиск или фильтр." : "Переадресаций нет. Они появятся сами, когда вы смените адрес страницы, товара или статьи."}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className={cn(LIST_FRAME_CLASS, "overflow-hidden")}>
        <div className={cn("type-caption hidden px-4 py-2 text-on-surface-variant", REDIRECT_ROW_GRID)} aria-hidden>
          <span>Откуда</span>
          <span>→ Куда</span>
          <span>Откуда взялась</span>
          <span />
        </div>
        <ul className="flex flex-col divide-y divide-[var(--outline-variant)]" aria-busy={api.loading}>
          {api.items.map((item) => (
            <RedirectRow key={item.id} item={item} canEdit={canEdit} onEdit={onEdit} onProbe={onProbe} onRemove={onRemove} />
          ))}
        </ul>
      </div>
      {api.hasMore ? (
        <Button variant="outline" className="self-center max-sm:w-full" loading={api.loadingMore} onClick={() => void api.loadMore()}>
          Показать ещё
        </Button>
      ) : null}
    </div>
  );
}
